import React from 'react';
import type { ReportFormat, ReportDateRangePreset, ReportType } from '../../types/report.types';
import type { ExportPreferences } from '../../types/report.types';

const DATE_PRESETS: { id: ReportDateRangePreset; label: string }[] = [
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'this_quarter', label: 'This Quarter' },
  { id: 'last_quarter', label: 'Last Quarter' },
  { id: 'this_financial_year', label: 'This Financial Year' },
  { id: 'last_financial_year', label: 'Last Financial Year' },
  { id: 'this_calendar_year', label: 'This Calendar Year' },
  { id: 'last_calendar_year', label: 'Last Calendar Year' },
  { id: 'custom', label: 'Custom Date Range' },
];

const FORMATS: { id: ReportFormat; label: string }[] = [
  { id: 'pdf', label: 'PDF Document' },
  { id: 'excel', label: 'Excel Spreadsheet' },
  { id: 'csv', label: 'CSV Data' },
  { id: 'json', label: 'JSON Data' },
];

export interface BasicStepState {
  title: string;
  dateRangePreset: ReportDateRangePreset;
  customStartDate: string;
  customEndDate: string;
  format: ReportFormat;
  includeCharts: boolean;
  includeRawData: boolean;
}

interface Props {
  state: BasicStepState;
  onChange: (patch: Partial<BasicStepState>) => void;
  validationError?: string | null;
  reportType?: ReportType;
}

export function ReportWizardStepBasic({
  state,
  onChange,
  validationError,
  reportType,
}: Props): React.ReactElement {
  const chartsAvailable = state.format !== 'pdf';
  return (
    <div className="space-y-5">
      <div>
        <label htmlFor="report-title" className="mb-1 block text-sm font-medium text-gray-700">
          Report Title
        </label>
        <input
          id="report-title"
          type="text"
          value={state.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Enter custom report title"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-gray-700">Date Range</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {DATE_PRESETS.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="dateRange"
                checked={state.dateRangePreset === p.id}
                onChange={() => onChange({ dateRangePreset: p.id })}
                className="text-blue-600 focus:ring-blue-500"
              />
              {p.label}
            </label>
          ))}
        </div>
        {state.dateRangePreset === 'custom' && (
          <div className="mt-3 flex gap-2">
            <input
              type="date"
              aria-label="Start date"
              value={state.customStartDate}
              onChange={(e) => onChange({ customStartDate: e.target.value })}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <span className="self-center text-gray-500">to</span>
            <input
              type="date"
              aria-label="End date"
              value={state.customEndDate}
              onChange={(e) => onChange({ customEndDate: e.target.value })}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        )}
        {validationError && <p className="mt-2 text-sm text-red-600">{validationError}</p>}
      </fieldset>

      <div>
        <p className="mb-2 text-sm font-medium text-gray-700">Export Format</p>
        <div className="grid grid-cols-2 gap-2">
          {FORMATS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onChange({ format: f.id })}
              className={`rounded-lg border p-3 text-left text-sm font-medium ${
                state.format === f.id
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {chartsAvailable ? (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={state.includeCharts}
              onChange={(e) => onChange({ includeCharts: e.target.checked })}
              className="rounded text-blue-600 focus:ring-blue-500"
            />
            Include charts and visualisations
          </label>
        ) : (
          <p className="text-sm text-gray-600">
            Charts are not included in PDF exports. Choose Excel or JSON to export chart data.
          </p>
        )}
        {reportType === 'business_expense' && (
          <p className="text-sm text-gray-600">
            Business expense reports always include itemised transactions in the export.
          </p>
        )}
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={state.includeRawData}
            onChange={(e) => onChange({ includeRawData: e.target.checked })}
            className="rounded text-blue-600 focus:ring-blue-500"
          />
          Include raw transaction data
        </label>
      </div>
    </div>
  );
}

export function buildBasicDefaults(
  reportTitle: string,
  prefs?: ExportPreferences,
  reportType?: ReportType
): BasicStepState {
  const format = prefs?.defaultFormat ?? 'pdf';
  return {
    title: reportTitle,
    dateRangePreset: prefs?.defaultDatePreset ?? 'this_month',
    customStartDate: prefs?.customStartDate ?? '',
    customEndDate: prefs?.customEndDate ?? '',
    format,
    includeCharts: format !== 'pdf',
    includeRawData: reportType === 'business_expense',
  };
}
